import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, Search, Leaf, LogOut } from 'lucide-react';
import { useCart } from '../../hooks/useCart';
import { useAuthStore } from '../../store/useAuthStore';
import CartDrawer from '../cart/CartDrawer';
import ThemeToggle from './ThemeToggle';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { itemCount } = useCart();
  const { user, isAuthenticated, logout } = useAuthStore();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
    navigate('/');
  };

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/products', label: 'Products' },
    { to: '/about', label: 'About' },
    { to: '/contact', label: 'Contact' },
  ];

  return (
    <>
      <header className="bg-dark-dark dark:bg-white shadow-md sticky top-0 z-50 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center shadow-md group-hover:shadow-lg transition-all duration-300 group-hover:scale-105">
                <Leaf className="w-6 h-6 text-white" />
              </div>
              <span className="font-display font-bold text-lg md:text-2xl text-white dark:text-dark-dark transition-colors duration-300">
                Angel <span className="text-accent dark:text-primary">Cite</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-gray-300 dark:text-gray-600 hover:text-accent dark:hover:text-primary font-medium transition-colors duration-300 relative group"
                >
                  {link.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-accent dark:bg-primary transition-all duration-300 group-hover:w-full" />
                </Link>
              ))}
            </nav>

            {/* Search Bar - Desktop */}
            <form onSubmit={handleSearch} className="hidden lg:flex items-center">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-64 pl-10 pr-4 py-2.5 border border-dark-light dark:border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-accent dark:focus:ring-primary focus:border-transparent transition-all bg-dark dark:bg-white text-gray-200 dark:text-gray-800 placeholder-gray-500 dark:placeholder-gray-400"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 dark:text-gray-400 transition-colors duration-300" />
              </div>
            </form>

            {/* Actions */}
            <div className="flex items-center gap-3">
              {/* Theme Toggle */}
              <div className="hidden sm:block">
                <ThemeToggle />
              </div>

              {/* Auth Section */}
              {isAuthenticated ? (
                <>
                  {/* Account Dropdown Trigger */}
                  <Link
                    to="/account"
                    className="flex items-center gap-2 p-2 text-gray-300 dark:text-gray-600 hover:text-accent dark:hover:text-primary transition-colors duration-300"
                  >
                    <div className="w-8 h-8 bg-accent/20 dark:bg-primary/20 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-accent dark:text-primary" />
                    </div>
                    <span className="hidden lg:block text-sm font-medium">
                      {user?.firstName || 'Account'}
                    </span>
                  </Link>
                </>
              ) : (
                <Link
                  to="/login"
                  className="hidden sm:flex items-center gap-2 p-2 text-gray-300 dark:text-gray-600 hover:text-accent dark:hover:text-primary transition-colors duration-300"
                >
                  <User className="w-5 h-5" />
                  <span className="text-sm font-medium">Sign In</span>
                </Link>
              )}

              {/* Cart */}
              <button
                onClick={() => setIsCartOpen(true)}
                className="relative p-2 text-gray-300 dark:text-gray-600 hover:text-accent dark:hover:text-primary transition-colors duration-300"
                aria-label="Shopping Cart"
              >
                <ShoppingCart className="w-6 h-6" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-6 h-6 min-w-[24px] flex items-center justify-center bg-accent text-white text-xs font-bold rounded-full animate-fade-in shadow-sm">
                    {itemCount > 99 ? '99+' : itemCount}
                  </span>
                )}
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 text-gray-300 dark:text-gray-600 hover:text-accent dark:hover:text-primary transition-colors duration-300 md:hidden"
                aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-dark-dark dark:bg-white border-t border-dark-light dark:border-gray-100 animate-slide-up">
            <div className="px-4 py-4 space-y-4">
              {/* Mobile Search */}
              <form onSubmit={handleSearch} className="flex items-center">
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-dark-light dark:border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-accent dark:focus:ring-primary bg-dark dark:bg-white text-gray-200 dark:text-gray-800 placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300"
                  />
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 dark:text-gray-400" />
                </div>
              </form>

              {/* Mobile Theme Toggle */}
              <div className="flex justify-center py-2">
                <ThemeToggle />
              </div>

              {/* Mobile Nav Links */}
              <div className="space-y-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setIsMenuOpen(false)}
                    className="block py-3 px-4 text-gray-300 dark:text-gray-600 hover:text-accent dark:hover:text-primary hover:bg-dark-light dark:hover:bg-gray-50 font-medium transition-colors duration-300 rounded-lg"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>

              {/* Mobile Auth Section */}
              {isAuthenticated ? (
                <>
                  <Link
                    to="/account"
                    onClick={() => setIsMenuOpen(false)}
                    className="block py-3 px-4 text-gray-300 dark:text-gray-600 hover:text-accent dark:hover:text-primary hover:bg-dark-light dark:hover:bg-gray-50 font-medium transition-colors duration-300 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <User className="w-5 h-5" />
                      <span>My Account</span>
                    </div>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left py-3 px-4 text-red-400 hover:text-red-300 hover:bg-dark-light dark:hover:bg-gray-50 font-medium transition-colors duration-300 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <LogOut className="w-5 h-5" />
                      <span>Sign Out</span>
                    </div>
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setIsMenuOpen(false)}
                  className="block py-3 px-4 bg-accent/20 dark:bg-primary/20 text-accent dark:text-primary hover:bg-accent/30 dark:hover:bg-primary/30 font-medium transition-colors duration-300 rounded-lg text-center"
                >
                  <div className="flex items-center justify-center gap-2">
                    <User className="w-5 h-5" />
                    <span>Sign In / Register</span>
                  </div>
                </Link>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Cart Drawer */}
      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </>
  );
};

export default Header;

